import { z } from "zod";
import { uniqueBy } from "./unique";
import { DEFAULT_VAT_RATE_BP, MAX_AMOUNT_CENTS, roundCents, VAT_RATES_BP } from "./money";
import { attachmentSchema, countryCodeSchema, hsCodeSchema, listQuerySchema, siretSchema } from "./schemas";

// ── Lines & totals ───────────────────────────────────────────────────────────

export interface LineInput {
  quantity: number;
  unitPriceCents: number;
  discountBp: number;
  vatRateBp: number;
  unitCostCents?: number;
}

export interface VatBucket {
  rateBp: number;
  baseCents: number;
  vatCents: number;
}

/** A deposit invoice taken off a final invoice: its net at one VAT rate (DGFiP use case 21). */
export interface DeductionInput {
  vatRateBp: number;
  netCents: number;
}

/**
 * A credit note that settles what is left of its invoice takes the invoice's remainder exactly: its
 * global discount and its VAT per rate are the invoice's minus the credit notes before it. Computed
 * on its own lines they could differ by a rounding cent, and the credit notes would then exceed the
 * invoice (or leave a cent on it). Rates are keyed in basis points.
 */
export interface TotalsAdjustment {
  globalDiscountCents?: number;
  vatCentsByRate?: Record<string, number>;
  /**
   * Taxable base of the goods per VAT rate (after the discount, before shipping), pinned when the
   * document mirrors an external one: Shopify prices and discounts are TTC, and the HT rounded line
   * by line lands a cent away from the base the store taxed. The lines keep their own amounts.
   */
  baseCentsByRate?: Record<string, number>;
  /**
   * Shipping per VAT rate, frozen when it does not follow from the document's own lines: a credit
   * note's shipping is split like the shipping of the invoice it corrects (audit FIN-22).
   */
  shippingByRate?: Record<string, number>;
}

/**
 * VAT of the shipping charged on a document (audit FIN-22).
 * - `prorata` (the French rule for accessory costs, BOFiP TVA-BASE-10-20-10): shipping follows the
 *   VAT of the goods it carries, split over their rates in proportion to their net base (after the
 *   global discount). An order entirely at 0 % ships at 0 %. Nothing to weigh (no goods): standard
 *   rate. A credit note freezes its split in `TotalsAdjustment.shippingByRate` (its invoice's split).
 * - `standard`: shipping at the standard rate whatever the goods. Documents created before the rule
 *   changed (their stored totals are what was issued), and Shopify orders, whose shipping tax is the
 *   one the store charged.
 */
export const SHIPPING_VAT_RULES = ["prorata", "standard"] as const;
export type ShippingVatRule = (typeof SHIPPING_VAT_RULES)[number];
export interface ShippingVat {
  rule: ShippingVatRule;
}
export const STANDARD_SHIPPING_VAT: ShippingVat = { rule: "standard" };

/** Splits `cents` over `weights` (rate → base) by largest remainder; empty when nothing weighs. */
export function splitCentsByWeight(cents: number, weights: Map<number, number>): Map<number, number> {
  const entries = [...weights.entries()].filter(([, w]) => w > 0);
  const total = entries.reduce((s, [, w]) => s + w, 0);
  const out = new Map<number, number>();
  if (!total) return out;
  const shares = entries.map(([rate, w]) => {
    const exact = (cents * w) / total;
    return { rate, cents: Math.floor(exact), fraction: exact - Math.floor(exact) };
  });
  let left = cents - shares.reduce((s, x) => s + x.cents, 0);
  for (const share of shares.toSorted((a, b) => b.fraction - a.fraction || b.rate - a.rate)) {
    if (left <= 0) break;
    share.cents += 1;
    left -= 1;
  }
  for (const share of shares) if (share.cents) out.set(share.rate, share.cents);
  return out;
}

/** Shipping per VAT rate under a rule, given the goods base per rate of the document. */
export function shippingByRate(shippingCents: number, goodsByRate: Map<number, number>, shipping: ShippingVat) {
  if (!shippingCents) return new Map<number, number>();
  if (shipping.rule === "prorata") {
    const own = splitCentsByWeight(shippingCents, goodsByRate);
    if (own.size) return own;
  }
  return new Map([[DEFAULT_VAT_RATE_BP, shippingCents]]);
}

export interface Totals {
  /** Sum of line nets, before the global discount. */
  subtotalCents: number;
  globalDiscountCents: number;
  shippingCents: number;
  /** Deposit invoices already issued, taken off the base before VAT. */
  deductedCents: number;
  netCents: number;
  vatCents: number;
  grossCents: number;
  costCents: number;
  /** Net goods revenue minus cost (shipping excluded). */
  marginCents: number;
  marginRatio: number | null;
  vat: VatBucket[];
  /** Shipping per VAT rate (its share of each bucket base), largest rate first. */
  shippingVat: { rateBp: number; cents: number }[];
}

export function lineNet(line: Pick<LineInput, "quantity" | "unitPriceCents" | "discountBp">): number {
  return roundCents((line.quantity * line.unitPriceCents * (10_000 - line.discountBp)) / 10_000);
}

/**
 * Document totals. The global discount is spread over VAT rates in proportion to their base
 * (largest remainder for the cents);
 * VAT is then computed once per rate on the total base (not per line), as French invoices require.
 * Shipping is taxed per `shipping` (see `ShippingVat`).
 */
export function computeTotals(
  lines: LineInput[],
  globalDiscountBp = 0,
  shippingCents = 0,
  deductions: DeductionInput[] = [],
  adjustment: TotalsAdjustment | null = null,
  shipping: ShippingVat = STANDARD_SHIPPING_VAT,
): Totals {
  const nets = lines.map(lineNet);
  const subtotal = nets.reduce((s, n) => s + n, 0);
  const globalDiscount = adjustment?.globalDiscountCents ?? roundCents((subtotal * globalDiscountBp) / 10_000);

  const byRate = new Map<number, number>();
  lines.forEach((line, i) => byRate.set(line.vatRateBp, (byRate.get(line.vatRateBp) ?? 0) + (nets[i] ?? 0)));

  // Largest remainder: each rate takes the whole cents of its share, and the cents left go to the
  // largest fractions. No rate is ever given more discount than its base, so no base turns negative.
  const rates = [...byRate.keys()].toSorted((a, b) => b - a);
  const shares = rates.map((rate) => {
    const exact = subtotal ? (globalDiscount * (byRate.get(rate) ?? 0)) / subtotal : 0;
    return { rate, cents: Math.floor(exact), fraction: exact - Math.floor(exact) };
  });
  let centsLeft = globalDiscount - shares.reduce((s, x) => s + x.cents, 0);
  for (const share of shares.toSorted((a, b) => b.fraction - a.fraction || b.rate - a.rate)) {
    if (centsLeft <= 0) break;
    share.cents += 1;
    centsLeft -= 1;
  }
  const buckets = new Map<number, number>();
  for (const share of shares) buckets.set(share.rate, (byRate.get(share.rate) ?? 0) - share.cents);
  for (const [rate, cents] of Object.entries(adjustment?.baseCentsByRate ?? {})) buckets.set(Number(rate), cents);
  const goods = [...buckets.values()].reduce((s, b) => s + b, 0);
  const shipped = adjustment?.shippingByRate
    ? new Map(Object.entries(adjustment.shippingByRate).map(([rate, cents]) => [Number(rate), cents]))
    : shippingByRate(shippingCents, buckets, shipping);
  for (const [rate, cents] of shipped) buckets.set(rate, (buckets.get(rate) ?? 0) + cents);
  // Deposits already invoiced come off the base of their rate (never below zero): VAT is then due on
  // what remains, as the final invoice after a deposit requires.
  let deducted = 0;
  for (const d of deductions) {
    const base = buckets.get(d.vatRateBp) ?? 0;
    const taken = Math.max(0, Math.min(d.netCents, base));
    if (taken) buckets.set(d.vatRateBp, base - taken);
    deducted += taken;
  }

  const vat = [...buckets.entries()]
    .toSorted(([a], [b]) => b - a)
    .map(([rateBp, baseCents]) => ({
      rateBp,
      baseCents,
      vatCents: adjustment?.vatCentsByRate?.[String(rateBp)] ?? roundCents((baseCents * rateBp) / 10_000),
    }));
  const netCents = goods + shippingCents - deducted;
  const vatCents = vat.reduce((s, b) => s + b.vatCents, 0);
  const costCents = lines.reduce((s, l) => s + l.quantity * (l.unitCostCents ?? 0), 0);
  return {
    // With a pinned base, the subtotal is the base plus the discount, so the printed totals add up.
    subtotalCents: goods + globalDiscount,
    globalDiscountCents: globalDiscount,
    shippingCents,
    deductedCents: deducted,
    netCents,
    vatCents,
    grossCents: netCents + vatCents,
    costCents,
    marginCents: goods - costCents,
    marginRatio: goods ? (goods - costCents) / goods : null,
    vat,
    shippingVat: [...shipped.entries()].toSorted(([a], [b]) => b - a).map(([rateBp, cents]) => ({ rateBp, cents })),
  };
}

// ── Schemas ──────────────────────────────────────────────────────────────────

const isoDate = z.iso.date("errors.field.date");
/** A document's goods total, before discounts, stays within MAX_AMOUNT_CENTS. */
export const withinMaxTotal = (amounts: number[]) => amounts.reduce((s, a) => s + a, 0) <= MAX_AMOUNT_CENTS;
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, "errors.field.tooLong")
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();
const bp = z.coerce.number().int().min(0, "errors.field.positive").max(10_000, "errors.field.number");

export const documentLineSchema = z.object({
  variantId: z.uuid().nullable().optional(),
  sku: optionalText(64),
  label: z.string().trim().min(1, "errors.field.required").max(500, "errors.field.tooLong"),
  /** Free text printed under the label; travels from the quote to the order and the invoice. */
  description: optionalText(2000),
  quantity: z.coerce.number().int("errors.field.number").min(1, "errors.field.positive").max(100_000),
  unitPriceCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(0, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge"),
  discountBp: bp.default(0),
  vatRateBp: z.coerce
    .number()
    .int()
    .refine((v) => (VAT_RATES_BP as readonly number[]).includes(v), "errors.field.number"),
});
export type DocumentLineInput = z.input<typeof documentLineSchema>;
export type DocumentLineData = z.output<typeof documentLineSchema>;

const documentBase = {
  notes: optionalText(5000),
  terms: optionalText(5000),
  shippingCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(0, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
    .default(0),
  lines: z
    .array(documentLineSchema)
    .min(1, "errors.field.required")
    .max(300)
    .refine((lines) => withinMaxTotal(lines.map((l) => l.quantity * l.unitPriceCents)), "errors.field.amountTooLarge"),
};

const quoteFields = z.object({
  customerId: z.uuid("errors.field.required"),
  issueDate: isoDate,
  validUntil: isoDate,
  reference: optionalText(200),
  /** Subject line, introduction paragraph and lead time printed on the quote (template texts when empty). */
  subject: optionalText(200),
  intro: optionalText(2000),
  deliveryTime: optionalText(200),
  globalDiscountBp: bp.default(0),
  ...documentBase,
});
/** A quote cannot expire before it is issued. */
const validAfterIssue = (v: { issueDate: string; validUntil: string }) => v.validUntil >= v.issueDate;
const validityIssue = { message: "errors.field.validityBeforeIssue", path: ["validUntil"] };

export const quoteSchema = quoteFields.refine(validAfterIssue, validityIssue);
export type QuoteInput = z.input<typeof quoteSchema>;
export type QuoteData = z.output<typeof quoteSchema>;

/** A quote as typed in the editor, drawn before it is saved; `id` gives the number and language of an existing one. */
export const quotePreviewSchema = quoteFields.extend({ id: z.uuid().optional() }).refine(validAfterIssue, validityIssue);
export type QuotePreviewData = z.output<typeof quotePreviewSchema>;

/**
 * Administrative references of an order from a public body, known once its purchase order arrives:
 * the commitment number (Chorus Pro BT-13), the SIRET and service code to invoice, the order number.
 * Empty SIRET and service code fall back to the customer's.
 */
export const publicOrderRefsSchema = z.object({
  engagementNumber: optionalText(50),
  billingSiret: siretSchema,
  chorusServiceCode: optionalText(100),
  purchaseOrderNumber: optionalText(100),
});
export type PublicOrderRefsInput = z.input<typeof publicOrderRefsSchema>;
export type PublicOrderRefsData = z.output<typeof publicOrderRefsSchema>;

/** A deposit invoice asked for: a share of the order (basis points) or an amount including VAT. */
export const depositRequestSchema = z.union([
  z.object({ bp: z.coerce.number().int("errors.field.number").min(1, "errors.field.positive").max(9999, "errors.field.number") }),
  z.object({
    amountCents: z.coerce
      .number()
      .int("errors.field.number")
      .min(1, "errors.field.positive")
      .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge"),
  }),
]);
export type DepositRequestInput = z.input<typeof depositRequestSchema>;
export type DepositRequest = z.output<typeof depositRequestSchema>;

/** A deposit invoice taken off a final invoice, as stored (and frozen) on that invoice. */
export interface DepositDeduction {
  invoiceId: string;
  number: string;
  issueDate: string;
  /** Net taken off at each VAT rate, keyed by the rate in basis points. */
  byRate: Record<string, number>;
}
export const deductionInputs = (deductions: DepositDeduction[]): DeductionInput[] =>
  deductions.flatMap((d) => Object.entries(d.byRate).map(([rate, netCents]) => ({ vatRateBp: Number(rate), netCents })));

/**
 * Accepting a quote may carry the customer's purchase order (references and PDF) and the delivery
 * note they signed, and ask for a deposit invoice.
 */
export const quoteAcceptSchema = publicOrderRefsSchema.extend({
  purchaseOrder: attachmentSchema.nullable().optional(),
  signedDeliveryNote: attachmentSchema.nullable().optional(),
  deposit: depositRequestSchema.nullable().optional(),
});
export type QuoteAcceptInput = z.input<typeof quoteAcceptSchema>;
export type QuoteAcceptData = z.output<typeof quoteAcceptSchema>;

export const invoiceDraftSchema = z.object({
  customerId: z.uuid("errors.field.required"),
  issueDate: isoDate.nullable().optional(),
  dueDate: isoDate.nullable().optional(),
  ...documentBase,
});
export type InvoiceDraftInput = z.input<typeof invoiceDraftSchema>;
export type InvoiceDraftData = z.output<typeof invoiceDraftSchema>;

export const PAYMENT_METHOD_VALUES = ["transfer", "card", "check", "cash", "shopify", "other"] as const;

export const paymentSchema = z.object({
  amountCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(1, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge"),
  paidAt: isoDate,
  method: z.enum(PAYMENT_METHOD_VALUES),
  reference: optionalText(200),
});
export type PaymentInput = z.input<typeof paymentSchema>;

export const creditNoteSchema = z.object({
  /** Lines to credit; defaults to the whole invoice. */
  lines: z
    .array(z.object({ invoiceLineId: z.uuid(), quantity: z.coerce.number().int().min(1, "errors.field.positive") }))
    .optional(),
  reason: optionalText(1000),
});
export type CreditNoteInput = z.input<typeof creditNoteSchema>;
export type CreditNoteData = z.output<typeof creditNoteSchema>;

export const sendDocumentSchema = z.object({
  to: z.email("errors.field.email"),
  cc: z
    .union([z.literal(""), z.email("errors.field.email")])
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional(),
  subject: z.string().trim().min(1, "errors.field.required").max(300, "errors.field.tooLong"),
  message: z.string().trim().min(1, "errors.field.required").max(10_000, "errors.field.tooLong"),
});
export type SendDocumentInput = z.input<typeof sendDocumentSchema>;

export const QUOTE_STATUS_VALUES = ["draft", "sent", "accepted", "declined", "expired", "cancelled"] as const;
/** Final invoice, credit note, or deposit invoice (asked for before delivery, taken off the final one). */
export const INVOICE_KIND_VALUES = ["invoice", "credit_note", "deposit"] as const;
export type InvoiceKindValue = (typeof INVOICE_KIND_VALUES)[number];
export const INVOICE_STATUS_VALUES = ["draft", "issued", "cancelled"] as const;
/**
 * Who issued the document: the ERP (numbered here), Shopify (Simple Invoice, numbered by order name)
 * or IABAKO, the previous tool (taken over with its own number and lines, its PDF stays in IABAKO).
 */
export const INVOICE_ISSUER_VALUES = ["erp", "shopify", "iabako"] as const;
export type InvoiceIssuer = (typeof INVOICE_ISSUER_VALUES)[number];
/** Quotes made in the ERP (DEV series) or taken over from IABAKO with their own number. */
export const QUOTE_ISSUER_VALUES = ["erp", "iabako"] as const;
/**
 * How a paid web order is invoiced: recorded from the invoice Shopify already issued (external),
 * issued and numbered by the ERP (erp), or left to a manual "Facturer" (manual).
 */
export const WEB_INVOICE_MODE_VALUES = ["external", "erp", "manual"] as const;
export type WebInvoiceMode = (typeof WEB_INVOICE_MODE_VALUES)[number];

export const quoteQuerySchema = listQuerySchema.extend({
  status: z.enum(QUOTE_STATUS_VALUES).optional(),
  customerId: z.uuid().optional(),
});

export const invoiceQuerySchema = listQuerySchema.extend({
  kind: z.enum(INVOICE_KIND_VALUES).optional(),
  status: z.enum(INVOICE_STATUS_VALUES).optional(),
  payment: z.enum(["unpaid", "overdue", "paid"]).optional(),
  issuer: z.enum(INVOICE_ISSUER_VALUES).optional(),
  customerId: z.uuid().optional(),
});

/** Longest period of the accountant's PDF export, both days included: one year, leap day too. */
export const INVOICE_EXPORT_MAX_DAYS = 366;

/** Most invoices one bulk download may hold (the ids travel in the query string). */
export const INVOICE_EXPORT_MAX_IDS = 200;
const includeExternal = z.enum(["0", "1"]).optional();
/** A period for the accountant, or a hand-picked selection; Shopify duplicates only when asked for. */
export const invoiceExportQuerySchema = z.union([
  z.object({
    // The API framework may hand a comma-separated query value over as an array: both are one list.
    ids: z.preprocess((v) => (Array.isArray(v) ? v.join(",") : v), z.string().regex(/^[0-9a-f-]{36}(,[0-9a-f-]{36}){0,199}$/i)),
    includeExternal,
  }),
  z
    .object({ from: isoDate, to: isoDate, includeExternal })
    .refine((v) => v.from <= v.to, { message: "errors.field.dateRange", path: ["to"] }),
]);
export type InvoiceExportQuery = z.output<typeof invoiceExportQuerySchema>;

export const companySettingsSchema = z.object({
  legalName: z.string().trim().min(1, "errors.field.required").max(200),
  tradeName: z.string().trim().min(1, "errors.field.required").max(200),
  legalForm: optionalText(60),
  capitalCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(0, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
    .nullable()
    .optional(),
  siren: optionalText(20),
  rcs: optionalText(120),
  vatNumber: optionalText(20),
  eori: optionalText(17),
  customsOriginCountry: countryCodeSchema,
  customsHsCode: hsCodeSchema,
  address: z
    .object({
      line1: z.string().trim().min(1, "errors.field.required"),
      line2: z.string().trim().nullable().optional(),
      postalCode: z.string().trim().min(1, "errors.field.required"),
      city: z.string().trim().min(1, "errors.field.required"),
      country: z.string().length(2),
    })
    .nullable()
    .optional(),
  email: z
    .union([z.literal(""), z.email("errors.field.email")])
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional(),
  phone: optionalText(40),
  website: optionalText(200),
  iban: optionalText(40),
  bic: optionalText(15),
  quoteValidityDays: z.coerce.number().int().min(1).max(365),
  defaultPaymentTermsDays: z.coerce.number().int().min(0).max(180),
  marginAlertBp: bp,
  archiveAfterDays: z.coerce.number().int().min(1).max(365),
  /** An after-sales case still open after this many days is flagged on the dashboard. */
  savAlertDays: z.coerce.number().int().min(1).max(90),
  accountingEmail: z
    .union([z.literal(""), z.email("errors.field.email")])
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional(),
  webInvoiceMode: z.enum(WEB_INVOICE_MODE_VALUES),
  creditNoteEngagement: z.boolean(),
  /** Deposit invoices offered at quote acceptance and on orders, with the share proposed by default. */
  depositInvoices: z.boolean(),
  depositDefaultBp: z.coerce.number().int("errors.field.number").min(1, "errors.field.positive").max(9999, "errors.field.number"),
  /** Each supplier's replenishment draft is kept filled by the products under their alert threshold. */
  autoReplenish: z.boolean().optional(),
  /** Set through Settings › Serial numbers (its own endpoint checks the stock first); read-only here. */
  serialTracking: z.boolean().optional(),
  /** Set through Settings › Families; read-only here. */
  productFamilies: z.array(z.string()).optional(),
  /** Set through its own endpoint; read-only here. */
  savReasons: z.array(z.any()).optional(),
  /** Set through Settings › Identity › Delivery addresses (its own endpoint); read-only here. */
  deliveryAddresses: z.array(z.any()).optional(),
});

export const COMPANY_LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const COMPANY_LOGO_TYPES = ["image/png", "image/jpeg"] as const;
/** Logo printed on quotes, invoices, delivery notes and purchase orders (PNG or JPEG, 2 MB at most). */
export const companyLogoSchema = z.object({
  type: z.enum(COMPANY_LOGO_TYPES, "errors.field.imageType"),
  dataBase64: z
    .string()
    .min(1, "errors.field.required")
    .max(Math.ceil((COMPANY_LOGO_MAX_BYTES * 4) / 3) + 4, "errors.field.fileTooLarge"),
});
export type CompanySettingsInput = z.input<typeof companySettingsSchema>;
export type CompanySettingsData = z.output<typeof companySettingsSchema>;

export type PaymentData = z.output<typeof paymentSchema>;

export const ORDER_VIEW_VALUES = [
  "open",
  "to_process",
  "preparing",
  "shipped",
  "delivered",
  "archived",
  "cancelled",
  "to_invoice",
  "all",
] as const;
export const ORDER_SOURCE_VALUES = ["shopify", "quote", "manual"] as const;

export const orderQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  view: z.enum(ORDER_VIEW_VALUES).default("open"),
  source: z.enum(ORDER_SOURCE_VALUES).optional(),
  flag: z.enum(["urgent", "stockout", "ready", "remainder"]).optional(),
  customerId: z.uuid().optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

/** Manual order (phone, e-mail, trade show) with no prior quote. */
export const manualOrderSchema = z.object({
  customerId: z.uuid("errors.field.required"),
  externalReference: optionalText(200),
  globalDiscountBp: bp.default(0),
  ...documentBase,
});
export type ManualOrderInput = z.input<typeof manualOrderSchema>;

/** « Isoler les articles »: the units of each line to set aside for the order (0 releases them). */
export const orderAllocationSchema = z.object({
  lines: z
    .array(
      z.object({
        lineId: z.uuid(),
        quantity: z.coerce.number().int("errors.field.number").min(0, "errors.field.positive").max(100_000),
      }),
    )
    .min(1, "errors.field.required")
    .refine(...uniqueBy("lineId"))
    .max(200),
});
export type OrderAllocationInput = z.input<typeof orderAllocationSchema>;
